import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import './Contact.css';
import { FaPlus, FaMinus } from 'react-icons/fa';
import AnimatedOnScroll from '../components/AnimatedOnScroll'; // <-- NEW IMPORT

// Accordion Item Component for FAQ
// We use AnimatedOnScroll here as well for a nice staggered effect
const AccordionItem = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <AnimatedOnScroll className="faq-item" delay={delay}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <h3>{question}</h3>
        <span>{isOpen ? <FaMinus /> : <FaPlus />}</span>
      </div>
      <motion.div
        className="faq-answer"
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0, padding: isOpen ? '1rem' : '0 1rem' }}
        transition={{ duration: 0.3 }}
      >
        <p>{answer}</p>
      </motion.div>
    </AnimatedOnScroll>
  );
};


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
  const [responseMsg, setResponseMsg] = useState('');

  // Debugging: Check if latest code is deployed
  React.useEffect(() => {
    console.log("Contact Component Loaded - Version: Fix-Email-Production-v1");
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Determine API URL based on environment
  const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://127.0.0.1:8000'
    : ''; // In production (served by Django), use relative path

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setResponseMsg('');

    try {
      // Use the explicit URL + endpoint
      const response = await axios.post(`${API_BASE_URL}/api/contact/submit/`, formData);

      if (response.status === 201 || response.status === 200) {
        setStatus('success');
        setResponseMsg('Your message has been sent. Thank you!');
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (err) {
      console.error("Submission Error:", err);
      setStatus('error');
      // Show more detailed error if available
      if (err.response) {
        const backendError = err.response.data.error ? ` (${err.response.data.error})` : '';
        setResponseMsg(`Error: ${err.response.data.detail || 'Failed to send message.'}${backendError}`);
      } else if (err.request) {
        setResponseMsg('Network Error. Please check your connection or server status.');
      } else {
        setResponseMsg('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ paddingTop: '100px' }}
    >
      <div className="container">
        {/* 1. Header (Uses on-load animation) */}
        <div className="contact-header">
          <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}>Get in Touch</motion.h1>
          <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
            We'd love to hear from you. Let's build something amazing together.
          </motion.p>
        </div>

        {/* 2. Form & Info Section (Refactored) */}
        <div className="contact-wrapper">
          <AnimatedOnScroll className="contact-info" delay={0.2}>
            <h3>Contact Information</h3>
            <p>
              Fill out the form, and our team will get back to you within 24 hours.
              Or, reach us directly through one of the channels below.
            </p>
            <div className="info-item">
              <strong>Email:</strong>
              <a href="mailto:apexiolabs.in@gmail.com" data-hover>apexiolabs.in@gmail.com</a>
            </div>
            <div className="info-item">
              <strong>Phone:</strong>
              <a href="tel:+917736664155" data-hover>+91 7736664155</a>
            </div>
            <div className="info-item">
              <strong>Address:</strong>
              <p>Mohammed Anjoom, Valliyengal House, Choonur, Chengottoor Post, 676503.</p>
            </div>
          </AnimatedOnScroll>

          <AnimatedOnScroll className="contact-form" delay={0.3}>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea name="message" id="message" rows="5" value={formData.message} onChange={handleChange} required></textarea>
              </div>
              <button type="submit" className="btn" data-hover disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && <p className="status-msg success">{responseMsg}</p>}
              {status === 'error' && <p className="status-msg error">{responseMsg}</p>}
            </form>
          </AnimatedOnScroll>
        </div>
      </div>

      {/* 3. FAQ Section (Refactored) */}
      <section className="faq-section">
        <div className="container">
          <AnimatedOnScroll className="centered-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
          </AnimatedOnScroll>
          <div className="faq-list">
            <AccordionItem
              delay={0.1}
              question="What technologies do you specialize in?"
              answer="We are full-stack experts, but our core technologies are the MERN stack (MongoDB, Express, React, Node.js) and the Python/Django stack. We also specialize in AI/ML, cloud services on AWS, and native mobile development."
            />
            <AccordionItem
              delay={0.2}
              question="How long does a typical project take?"
              answer="Project timelines vary greatly depending on the scope. A simple website might take 4-6 weeks, while a complex enterprise application could take 6-12 months. We provide a detailed project roadmap and timeline after our initial discovery phase."
            />
            <AccordionItem
              delay={0.3}
              question="What is your development process?"
              answer="We follow an Agile methodology. Our process includes 1) Discovery & Strategy, 2) UI/UX Design & Prototyping, 3) Development in Sprints, 4) Quality Assurance & Testing, and 5) Deployment & Ongoing Support."
            />
            <AccordionItem
              delay={0.4}
              question="How much will my project cost?"
              answer="We provide custom quotes based on your specific requirements. After a free 30-minute consultation and requirements analysis, we can provide a detailed estimate for your project."
            />
          </div>
        </div>
      </section>


    </motion.div>
  );
};

export default Contact;